import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { useEffect, useState, createContext, useContext, useCallback } from 'react';
import { auth } from './firebase';
import { Assinatura, assinaturasApi } from './api/assinaturas';
import { UsuarioApi, usuariosApi } from './api/usuarios';
import { NovitaApiError } from './api/client';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import WhatsAppButton from './components/WhatsAppButton';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Triage from './pages/Triage';
import Consultation from './pages/Consultation';
import Inventory from './pages/Inventory';
import Profile from './pages/Profile';
import PrescriptionView from './pages/PrescriptionView';
import Blocked from './pages/Blocked';

interface AuthContextType {
  user: UsuarioApi | null;
  assinatura: Assinatura | null;
  loading: boolean;
  recarregarAssinatura: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  assinatura: null,
  loading: true,
  recarregarAssinatura: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export default function App() {
  const [user, setUser] = useState<UsuarioApi | null>(null);
  const [assinatura, setAssinatura] = useState<Assinatura | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Estratégia de bloqueio: a fonte da verdade é o status retornado por
   * GET /api/assinaturas/minha. NUNCA confiamos em flag do Firestore para
   * gating, pois cliente pode manipular. A API determina ATIVA/INADIMPLENTE/SUSPENSA.
   */
  const carregarUsuarioEAssinatura = useCallback(async () => {
    try {
      const me = await usuariosApi.me();
      setUser(me);
      try {
        const sub = await assinaturasApi.minha();
        setAssinatura(sub);
      } catch (err) {
        if (err instanceof NovitaApiError && err.status !== 404) {
          console.error('Erro ao carregar assinatura:', err);
        }
        setAssinatura(null);
      }
    } catch (err) {
      // Usuário Firebase autenticado mas ainda não sincronizado com a API.
      // Pode ser primeiro acesso pós-cadastro — Signup faz o sync.
      if (err instanceof NovitaApiError && err.status === 401) {
        setUser(null);
      } else {
        console.error('Erro ao carregar usuário:', err);
      }
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        await carregarUsuarioEAssinatura();
      } else {
        setUser(null);
        setAssinatura(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, [carregarUsuarioEAssinatura]);

  /**
   * Determina se o paciente está bloqueado.
   * Médicos/atendentes/admins NUNCA são bloqueados — não pagam assinatura.
   * Paciente fica bloqueado se:
   *   - status_acesso é "bloqueado" (admin baniu) OU
   *   - assinatura é SUSPENSA ou CANCELADA
   * INADIMPLENTE ainda permite acesso (período de cortesia).
   */
  function isBlocked(): boolean {
    if (!user) return false;
    if (user.perfil !== 'paciente') return false;
    if (user.status_acesso === 'bloqueado') return true;
    if (assinatura?.status === 'suspensa' || assinatura?.status === 'cancelada') return true;
    return false;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-bg-light">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const userIsBlocked = isBlocked();

  return (
    <ErrorBoundary>
      <AuthContext.Provider
        value={{
          user,
          assinatura,
          loading,
          recarregarAssinatura: carregarUsuarioEAssinatura,
        }}
      >
        <Router>
          <div className="min-h-screen flex flex-col bg-bg-light font-sans">
            <Navbar />
            <main className="flex-grow">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route
                  path="/login"
                  element={!user ? <Login /> : <Navigate to="/dashboard" />}
                />
                <Route
                  path="/signup"
                  element={!user ? <Signup /> : <Navigate to="/dashboard" />}
                />
                <Route
                  path="/blocked"
                  element={userIsBlocked ? <Blocked /> : <Navigate to="/dashboard" />}
                />
                <Route
                  path="/dashboard"
                  element={
                    user ? (
                      userIsBlocked ? <Navigate to="/blocked" /> : <Dashboard />
                    ) : (
                      <Navigate to="/login" />
                    )
                  }
                />
                <Route
                  path="/triage"
                  element={
                    user?.perfil === 'atendente' || user?.perfil === 'admin' ? (
                      <Triage />
                    ) : (
                      <Navigate to="/dashboard" />
                    )
                  }
                />
                <Route
                  path="/consultation/:id"
                  element={
                    user ? (
                      userIsBlocked ? <Navigate to="/blocked" /> : <Consultation />
                    ) : (
                      <Navigate to="/login" />
                    )
                  }
                />
                <Route
                  path="/inventory"
                  element={
                    user?.perfil === 'atendente' || user?.perfil === 'admin' ? (
                      <Inventory />
                    ) : (
                      <Navigate to="/dashboard" />
                    )
                  }
                />
                <Route
                  path="/profile"
                  element={user ? <Profile /> : <Navigate to="/login" />}
                />
                <Route
                  path="/prescription/:id"
                  element={
                    user ? (
                      userIsBlocked ? <Navigate to="/blocked" /> : <PrescriptionView />
                    ) : (
                      <Navigate to="/login" />
                    )
                  }
                />
              </Routes>
            </main>
            <Footer />
            <WhatsAppButton />
          </div>
        </Router>
      </AuthContext.Provider>
    </ErrorBoundary>
  );
}
